import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip, 
  Legend,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area,
  ComposedChart
} from 'recharts';
import { Tournament, Match, Team } from '@/types/cricket';
import { format } from 'date-fns';

interface TournamentOverviewProps {
  tournaments: Tournament[];
  matches: Match[];
  teams: Team[];
  selectedTournamentId?: string;
}

const COLORS = ['#0A5F38', '#3498DB', '#E74C3C', '#F39C12', '#9B59B6', '#1ABC9C', '#34495E', '#E67E22'];

const TournamentOverview: React.FC<TournamentOverviewProps> = ({ 
  tournaments, 
  matches, 
  teams, 
  selectedTournamentId 
}) => {
  const selectedTournament = selectedTournamentId 
    ? tournaments.find(t => t.id === selectedTournamentId)
    : tournaments.find(t => t.name === 'Summer Cricket League - New') || tournaments[0];

  const tournamentMatches = useMemo(() => {
    if (!selectedTournament) return [];
    return matches.filter(m => m.tournamentId === selectedTournament.id);
  }, [matches, selectedTournament]);

  const tournamentTeams = useMemo(() => {
    if (!selectedTournament) return [];
    return teams.filter(t => t.tournamentId === selectedTournament.id);
  }, [teams, selectedTournament]);

  // Tournament Progress Data
  const progressData = useMemo(() => {
    const totalMatches = tournamentMatches.length;
    const completedMatches = tournamentMatches.filter(m => m.status === 'completed').length;
    const liveMatches = tournamentMatches.filter(m => m.status === 'live').length;
    const upcomingMatches = tournamentMatches.filter(m => m.status === 'upcoming').length;
    
    return {
      total: totalMatches,
      completed: completedMatches,
      live: liveMatches,
      upcoming: upcomingMatches,
      progressPercentage: totalMatches > 0 ? (completedMatches / totalMatches) * 100 : 0
    };
  }, [tournamentMatches]);

  // Match Schedule Timeline Data
  const scheduleData = useMemo(() => {
    return tournamentMatches
      .sort((a, b) => new Date(a.matchDate).getTime() - new Date(b.matchDate).getTime())
      .map(match => {
        const team1 = teams.find(t => t.id === match.team1Id);
        const team2 = teams.find(t => t.id === match.team2Id);
        return {
          date: format(new Date(match.matchDate), 'MMM dd'),
          fullDate: match.matchDate,
          matchup: `${team1?.shortCode || 'T1'} vs ${team2?.shortCode || 'T2'}`,
          city: match.city,
          status: match.status,
          statusColor: match.status === 'completed' ? '#0A5F38' : 
                      match.status === 'live' ? '#E74C3C' : '#3498DB'
        };
      });
  }, [tournamentMatches, teams]);

  // Match Outcome Summary Data
  const outcomeData = useMemo(() => {
    const outcomes = {
      completed: tournamentMatches.filter(m => m.status === 'completed').length,
      live: tournamentMatches.filter(m => m.status === 'live').length,
      upcoming: tournamentMatches.filter(m => m.status === 'upcoming').length
    };
    
    return [
      { status: 'Completed', count: outcomes.completed, color: '#0A5F38' },
      { status: 'Live', count: outcomes.live, color: '#E74C3C' },
      { status: 'Upcoming', count: outcomes.upcoming, color: '#3498DB' }
    ];
  }, [tournamentMatches]);

  // Team Performance Data
  const teamPerformanceData = useMemo(() => {
    const teamStats = tournamentTeams.map(team => {
      const teamMatches = tournamentMatches.filter(m => 
        m.team1Id === team.id || m.team2Id === team.id
      );
      
      let wins = 0;
      teamMatches.forEach(match => {
        if (match.status === 'completed') {
          if (match.team1Id === team.id && match.team1Score.runs > match.team2Score.runs) wins++;
          if (match.team2Id === team.id && match.team2Score.runs > match.team1Score.runs) wins++;
        }
      });
      
      return {
        teamName: team.shortCode || team.name,
        wins,
        matches: teamMatches.length
      };
    });
    
    return teamStats.sort((a, b) => b.wins - a.wins);
  }, [tournamentTeams, tournamentMatches]);

  // Match-wise Run Comparison Data
  const runComparisonData = useMemo(() => {
    return tournamentMatches
      .filter(m => m.status === 'completed')
      .sort((a, b) => new Date(a.matchDate).getTime() - new Date(b.matchDate).getTime())
      .slice(0, 10) // Show last 10 completed matches
      .map(match => {
        const team1 = teams.find(t => t.id === match.team1Id);
        const team2 = teams.find(t => t.id === match.team2Id);
        return {
          matchup: `${team1?.shortCode || 'T1'} vs ${team2?.shortCode || 'T2'}`,
          team1Runs: match.team1Score.runs,
          team2Runs: match.team2Score.runs,
          date: format(new Date(match.matchDate), 'MMM dd')
        };
      });
  }, [tournamentMatches, teams]);

  // City Usage Distribution Data
  const cityData = useMemo(() => {
    const cityCount = tournamentMatches.reduce((acc, match) => {
      acc[match.city] = (acc[match.city] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    return Object.entries(cityCount).map(([city, count]) => ({
      city,
      count,
      percentage: ((count / tournamentMatches.length) * 100).toFixed(1)
    }));
  }, [tournamentMatches]);

  // Match Types Breakdown Data
  const matchTypesData = useMemo(() => {
    const typeCount = tournamentMatches.reduce((acc, match) => {
      const type = match.matchType || 'T20';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    return Object.entries(typeCount).map(([type, count]) => ({
      type,
      count,
      percentage: ((count / tournamentMatches.length) * 100).toFixed(1)
    }));
  }, [tournamentMatches]);

  // Matches Over Time Data
  const matchesOverTimeData = useMemo(() => {
    const dateCount = tournamentMatches.reduce((acc, match) => {
      const date = format(new Date(match.matchDate), 'MMM dd');
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    return Object.entries(dateCount)
      .map(([date, count]) => ({ date, matches: count }))
      .sort((a, b) => new Date(a.date + ', 2024').getTime() - new Date(b.date + ', 2024').getTime());
  }, [tournamentMatches]);

  return (
    <div className="space-y-6">
      {/* Tournament Progress Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Tournament Progress Overview - {selectedTournament?.name || 'Summer Cricket League'}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Progress</span>
              <span className="text-sm text-gray-500">
                {progressData.completed}/{progressData.total} matches completed
              </span>
            </div>
            {/* Custom Progress Bar */}
            <div className="flex w-full h-3 rounded-full overflow-hidden">
              {progressData.total > 0 ? (
                <>
                  <div 
                    className="bg-green-700 h-full transition-all duration-700 ease-out" 
                    style={{ width: `${(progressData.completed / progressData.total) * 100}%` }}
                  ></div>
                  <div 
                    className="bg-red-700 h-full transition-all duration-700 ease-out" 
                    style={{ width: `${(progressData.live / progressData.total) * 100}%` }}
                  ></div>
                  <div 
                    className="bg-blue-700 h-full transition-all duration-700 ease-out" 
                    style={{ width: `${(progressData.upcoming / progressData.total) * 100}%` }}
                  ></div>
                </>
              ) : (
                <div className="bg-gray-400 h-full w-full transition-all duration-700 ease-out"></div>
              )}
            </div>
            <div className="flex gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span>Completed: {progressData.completed}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <span>Live: {progressData.live}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <span>Upcoming: {progressData.upcoming}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Match Outcome Summary */}
        <Card>
          <CardHeader>
            <CardTitle>Match Outcome Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={outcomeData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                  label={({ status, count }) => `${status}: ${count}`}
                >
                  {outcomeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Team Performance in Tournament */}
        <Card>
          <CardHeader>
            <CardTitle>Team Performance (Wins)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={teamPerformanceData}>
                <XAxis dataKey="teamName" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="wins" name="Wins" fill="#0A5F38" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* City Usage Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>City Usage Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={cityData} layout="horizontal">
                <XAxis type="number" />
                <YAxis dataKey="city" type="category" width={100} />
                <Tooltip />
                <Bar dataKey="count" name="Matches" fill="#3498DB" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Match Types Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Match Types Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={matchTypesData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                  label={({ type, percentage }) => `${type}: ${percentage}%`}
                >
                  {matchTypesData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Match-wise Run Comparison */}
      <Card>
        <CardHeader>
          <CardTitle>Match-wise Run Comparison (Recent Completed Matches)</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={runComparisonData}>
              <XAxis dataKey="matchup" angle={-45} textAnchor="end" height={80} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="team1Runs" name="Team 1 Runs" fill="#0A5F38" />
              <Bar dataKey="team2Runs" name="Team 2 Runs" fill="#3498DB" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Matches Over Time */}
      <Card>
        <CardHeader>
          <CardTitle>Matches Over Time</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={matchesOverTimeData}>
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Area type="monotone" dataKey="matches" stroke="#0A5F38" fill="#0A5F38" fillOpacity={0.6} />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Tournament Summary Table */}
      <Card>
        <CardHeader>
          <CardTitle>Tournament Summary Table</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-border">
              <thead>
                <tr className="bg-muted">
                  <th className="border border-border px-4 py-2 text-left">Date</th>
                  <th className="border border-border px-4 py-2 text-left">Teams</th>
                  <th className="border border-border px-4 py-2 text-left">City</th>
                  <th className="border border-border px-4 py-2 text-left">Type</th>
                  <th className="border border-border px-4 py-2 text-left">Status</th>
                  <th className="border border-border px-4 py-2 text-left">Score</th>
                </tr>
              </thead>
              <tbody>
                {scheduleData.map((match, index) => (
                  <tr key={index} className="hover:bg-accent/50">
                    <td className="border border-border px-4 py-2">{match.date}</td>
                    <td className="border border-border px-4 py-2">{match.matchup}</td>
                    <td className="border border-border px-4 py-2">{match.city}</td>
                    <td className="border border-border px-4 py-2">
                      {tournamentMatches.find(m => 
                        format(new Date(m.matchDate), 'MMM dd') === match.date &&
                        match.matchup.includes(teams.find(t => t.id === m.team1Id)?.shortCode || '')
                      )?.matchType || 'T20'}
                    </td>
                    <td className="border border-border px-4 py-2">
                      <Badge 
                        variant={match.status === 'completed' ? 'default' : 
                                match.status === 'live' ? 'destructive' : 'secondary'}
                      >
                        {match.status}
                      </Badge>
                    </td>
                    <td className="border border-border px-4 py-2">
                      {match.status === 'completed' ? (
                        (() => {
                          const m = tournamentMatches.find(tm => 
                            format(new Date(tm.matchDate), 'MMM dd') === match.date &&
                            match.matchup.includes(teams.find(t => t.id === tm.team1Id)?.shortCode || '')
                          );
                          return m ? `${m.team1Score.runs}/${m.team1Score.wickets} - ${m.team2Score.runs}/${m.team2Score.wickets}` : '-';
                        })()
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TournamentOverview;
